import { useEffect, useState } from "react";

/**
 * RefEntryModal — reusable create / edit / delete modal for the reference
 * pages (Codes, Payees, Payments).
 *
 * Uses the shared modal CSS tokens (`modal-backdrop`, `modal-card`,
 * `modal-head`, `modal-form`, `form-grid`, `modal-actions`) so it visually
 * matches the Users / Clients / Ledgers / Transactions modals.
 *
 * Props:
 *  - mode: "create" | "edit"
 *  - title: modal title text
 *  - fields: [{ name, label, type?, maxLength?, required?, readOnly?,
 *              pk?, placeholder?, hint?, gridSpan? }]
 *  - initial: object with initial values keyed by field name
 *  - onSave:  async (payload) => void  (throws on error)
 *  - onDelete?: async () => void       (present only in edit mode when allowed)
 *  - onClose: () => void
 *  - canWrite: boolean — hides Save/Delete for non-admins
 *  - testIdPrefix: e.g. "codes-modal"
 */
export default function RefEntryModal({
  mode, title, fields, initial, onSave, onDelete, onClose,
  canWrite, testIdPrefix,
}) {
  const [form, setForm] = useState({});
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [confirmDel, setConfirmDel] = useState(false);

  useEffect(() => { setForm({ ...(initial || {}) }); }, [initial]);

  const set = (name, v) => setForm((f) => ({ ...f, [name]: v }));

  const submit = async (e) => {
    e?.preventDefault();
    setErr(""); setBusy(true);
    try {
      const payload = {};
      for (const f of fields) {
        // Never mutate the primary key on edit.
        if (mode === "edit" && f.pk) continue;
        payload[f.name] = form[f.name];
      }
      await onSave(payload);
    } catch (e2) {
      setErr(e2.response?.data?.detail || e2.message || "Save failed");
    } finally {
      setBusy(false);
    }
  };

  const handleDelete = async () => {
    if (!confirmDel) { setConfirmDel(true); return; }
    setErr(""); setBusy(true);
    try { await onDelete(); }
    catch (e2) {
      setErr(e2.response?.data?.detail || e2.message || "Delete failed");
      setBusy(false);
      setConfirmDel(false);
    }
  };

  return (
    <div
      className="modal-backdrop"
      data-testid={`${testIdPrefix}-backdrop`}
      onClick={onClose}
    >
      <div
        className="modal-card"
        onClick={(e) => e.stopPropagation()}
        style={{ maxWidth: 640 }}
        data-testid={`${testIdPrefix}-dialog`}
      >
        <div className="modal-head">
          <h2>{title}</h2>
          <button
            type="button"
            className="modal-close"
            aria-label="Close"
            data-testid={`${testIdPrefix}-close`}
            onClick={onClose}
          >×</button>
        </div>
        <form className="modal-form" onSubmit={submit}>
          <div className="form-grid">
            {fields.map((f) => {
              const style = f.gridSpan
                ? { gridColumn: `span ${f.gridSpan}` }
                : undefined;
              const lockPk = mode === "edit" && f.pk;
              return (
                <div className="form-field" key={f.name} style={style}>
                  <label>
                    {f.label}{f.required && <span className="req">*</span>}
                    {f.hint && (
                      <span
                        className="muted"
                        style={{ fontSize: 11, marginLeft: 6 }}
                      >{f.hint}</span>
                    )}
                  </label>
                  <input
                    data-testid={`${testIdPrefix}-input-${f.name}`}
                    type={f.type || "text"}
                    value={form[f.name] ?? ""}
                    maxLength={f.maxLength}
                    placeholder={f.placeholder}
                    required={f.required}
                    readOnly={f.readOnly || lockPk}
                    disabled={!canWrite || busy}
                    onChange={(e) => set(f.name, e.target.value)}
                  />
                </div>
              );
            })}
          </div>
          {err && (
            <div className="form-error" data-testid={`${testIdPrefix}-error`}>
              {err}
            </div>
          )}
          <div
            className="modal-actions no-divider"
            style={{ justifyContent: "space-between" }}
          >
            <div>
              {mode === "edit" && onDelete && canWrite && (
                <button
                  type="button"
                  className="btn-danger"
                  data-testid={confirmDel
                    ? `${testIdPrefix}-confirm-delete`
                    : `${testIdPrefix}-delete`}
                  disabled={busy}
                  onClick={handleDelete}
                  title={confirmDel ? "Click again to confirm" : "Delete this entry"}
                >{confirmDel ? "Click again to confirm" : "Delete"}</button>
              )}
            </div>
            <div style={{ display: "flex", gap: 10 }}>
              {canWrite && (
                <button
                  type="submit"
                  className="btn-primary"
                  data-testid={`${testIdPrefix}-save`}
                  disabled={busy}
                  style={{ width: "auto", padding: "10px 22px", margin: 0 }}
                >{busy ? "Saving…" : (mode === "create" ? "Create" : "Save")}</button>
              )}
            </div>
          </div>
        </form>
      </div>
    </div>
  );
}
